import { distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CheckRevenuesDataSeriesParkByPeriodGQL, CheckRevenuesFactsParkByPeriodGQL, ParksAndCountriesGQL,
  TimeGranularityEnum, CheckRevenuesFactsPortfolioByPeriodGQL, CheckRevenuesDataSeriesPortfolioByPeriodGQL, CheckRevenuesDataSeriesPortfolioByPeriodAndParkGQL, ParksCapturedPriceGQL
} from '../../graphql';
import { CheckRevenuesFacts } from 'app/models/check-revenues/check-revenues-facts.model';
import { CheckRevenuesDataSeries } from '../../../../../models/check-revenues/check-revenues-data-series.model';
import { CheckRevenuesParksCountries } from '../../../../../models/check-revenues/check-revenues-parks-countries.model';
import moment from 'moment';

@Injectable()
export class CheckRevenuesFactsService {
  constructor(
    private checkRevenuesFactsParkByPeriodGQL: CheckRevenuesFactsParkByPeriodGQL,
    private checkRevenuesDataSeriesParkByPeriodGQL: CheckRevenuesDataSeriesParkByPeriodGQL,
    private checkRevenuesFactsPortfolioByPeriodGQL: CheckRevenuesFactsPortfolioByPeriodGQL,
    private checkRevenuesDataSeriesPortfolioByPeriodGQL: CheckRevenuesDataSeriesPortfolioByPeriodGQL,
    private checkRevenuesDataSeriesPortfolioByPeriodAndParkGQL: CheckRevenuesDataSeriesPortfolioByPeriodAndParkGQL,
    private parksAndCountriesGQL: ParksAndCountriesGQL,
    private parkCapturedPriceGQL: ParksCapturedPriceGQL
  ) { }

  public getCheckRevenuesFactsParkByPeriodData(
    parkNameObservable: Observable<string>,
    dayFrom: number,
    dayTo: number,
    monthFrom: number,
    monthTo: number,
    monthToEnd: number,
    yearFrom: number,
    yearTo: number,
    yearToEnd: number
  ): Observable<CheckRevenuesFacts> {
    return parkNameObservable.pipe(
      distinctUntilChanged(),
      switchMap((name: string) =>
        this.checkRevenuesFactsParkByPeriodGQL
          .fetch({
            variables: {
            name,
            dayFrom,
            dayTo,
            monthFrom,
            monthTo,
            monthToEnd,
            yearFrom,
            yearTo,
            yearToEnd,
            }
          })
          .pipe(
            map((result) =>
              CheckRevenuesFacts.mapFromPark(result.data.parks[0])
            )
          )
      )
    );
  }

  public getCheckRevenuesDataSeriesParkByPeriodData(
    parkNameObservable: Observable<string>,
    dayFrom: number,
    dayTo: number,
    granularity: TimeGranularityEnum,
    timezone: string
  ): Observable<CheckRevenuesDataSeries> {
    return parkNameObservable.pipe(
      distinctUntilChanged(),
      switchMap((name: string) =>
        this.checkRevenuesDataSeriesParkByPeriodGQL
          .fetch({
            variables: {
            name,
            dayFrom,
            dayTo,
            granularity,
            timezone,}
          })
          .pipe(
            map((result) =>
              CheckRevenuesDataSeries.mapFromPark(JSON.parse(JSON.stringify(result.data.parks[0])), granularity)
            )
          )
      )
    );
  }

  public getCheckRevenuesFactsPortfolioByPeriodData(monthFrom: number, monthTo: number, monthToEnd: number,
    yearFrom: number, yearTo: number, yearToEnd: number, portfolioId: string, parks: string[]): Observable<CheckRevenuesFacts[]> {
    return this.checkRevenuesFactsPortfolioByPeriodGQL
      .fetch({variables: { monthFrom, monthTo, monthToEnd, yearFrom, yearTo, yearToEnd, portfolioId, parks }})
      .pipe(
        map((portfolioFacts) =>
          CheckRevenuesFacts.mapFromPortfolio(
            JSON.parse(JSON.stringify(portfolioFacts.data.portfolios[0].parks))
          )
        )
      );
  }

  public getCheckRevenuesDataSeriesPortfolioByPeriodData(monthFrom: number, monthTo: number,
    granularity: TimeGranularityEnum, timezone: string, portfolioId: string, isComparison = false, parks: string[]): Observable<CheckRevenuesDataSeries[]> {
    if (isComparison) {
      return this.checkRevenuesDataSeriesPortfolioByPeriodAndParkGQL
        .fetch({variables: { monthFrom, monthTo, granularity, timezone, portfolioId, parks }})
        .pipe(
          map((portfolioDataSeries) => {
            return CheckRevenuesDataSeries.mapFromPortfolio(
              JSON.parse(JSON.stringify(portfolioDataSeries.data.portfolios[0].parks)), [], granularity, isComparison)
          }
          )
        );
    } else {
      return this.checkRevenuesDataSeriesPortfolioByPeriodGQL
        .fetch({variables: { monthFrom, monthTo, granularity, timezone, portfolioId, parks }})
        .pipe(
          map((portfolioDataSeries) => {
            return CheckRevenuesDataSeries.mapFromPortfolio(
              JSON.parse(JSON.stringify(portfolioDataSeries.data.portfolios[0].parks)), JSON.parse(JSON.stringify(portfolioDataSeries.data.portfolios[0].energyMeterDataseries)), granularity, isComparison)
          }
          )
        );
    }
  }
  public getParksCapturedPrice(id: string, from: number, to: number, isReport: boolean = true, timezone= undefined) {
    timezone ??= Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.parkCapturedPriceGQL.fetch({variables: { id, from, to, timezone }})
      .pipe(map(result => result.data.parks.map((item) => {
        const priceComparison = { avgCapturedPrice: item.avgCapturedPrice, avgAreaPrice: item.avgAreaPrice, targetPrice: item.targetPrice }
        const windCaptureVSTarget = { windCaptureRatio: item.windCaptureRatio, targetCaptureRatio: item.targetCaptureRatio }
        const capturedPriceVSTarget = { avgCapturedPrice: item.avgCapturedPrice, targetPrice: item.targetPrice }
        return isReport ? [priceComparison, windCaptureVSTarget, capturedPriceVSTarget] : [priceComparison, windCaptureVSTarget]
      }
      )))
  }
}
